import React, { FC, useEffect, useState, useRef } from 'react';
import translate from '@translate';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import TopBlock from './TopBlock';
import { RootStoreType } from 'src/types/store';
import { allColumns } from './constants';
import TableSimple from 'src/components/TableSimple';
import { CustomRenderType, ExportTypeType, TableRefType } from 'src/types/table';
import { useCheckPermission } from 'src/utils/checkPermission';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import VerticalSplitIcon from '@mui/icons-material/VerticalSplit';
import {
    getBillsOfMaterialsListFetch,
    activateBillOfMaterialsFetch,
    deactivateBillOfMaterialsFetch,
} from 'src/resources/billsOfMaterialsList/reducer';
import SimpleSwitch from 'src/components/SimpleSwitch';
import { useParams } from 'react-router-dom';

const BillsOfMaterialsListContainer: FC = () => {
    const dispatch = useDispatch();
    const tableRef = useRef<TableRefType>();

    const { idProduct } = useParams();

    const loading = useSelector((state: RootStoreType) => state.billsOfMaterialList.loading);
    const billsOfMaterialData = useSelector((state: RootStoreType) => state.billsOfMaterialList.data);
    const boolNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.boolNumberCodes);

    const permissionCreate = useCheckPermission('BILL_OF_MATERIALS.CREATE');
    const permissionUpdate = useCheckPermission('BILL_OF_MATERIALS.UPDATE');
    const permissionDetail = useCheckPermission('BILL_OF_MATERIALS.DETAIL');

    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);

    useEffect(() => {
        if (idProduct) {
            dispatch(
                setLocationParams({
                    name: translate('billsOfMaterial.titlePrduct', { idProduct }),
                    backUrl: `/products/${idProduct}/`,
                })
            );
        } else {
            dispatch(setLocationParams({ name: translate('billsOfMaterial.title') }));
        }
        dispatch(getBillsOfMaterialsListFetch(idProduct));
    }, [idProduct]);

    const reloadBillsOfMaterialsList = () => {
        dispatch(getBillsOfMaterialsListFetch(idProduct));
    };

    const handleExport = (type: ExportTypeType) => {
        tableRef.current?.exportTable(type, translate('billsOfMaterial.action.exportFileName'));
    };

    const customRender: CustomRenderType = (column, row) => {
        return (
            <SimpleSwitch
                value={!!row.ACTIVE}
                onChange={(value) => {
                    if (value) {
                        dispatch(activateBillOfMaterialsFetch(row.ID));
                    } else {
                        dispatch(deactivateBillOfMaterialsFetch(row.ID));
                    }
                }}
                disabled={!permissionUpdate}
            />
        );
    };

    return (
        <>
            <TopBlock
                reloadBillsOfMaterialsList={reloadBillsOfMaterialsList}
                permissionCreate={permissionCreate}
                handleExport={handleExport}
                idProduct={idProduct}
            />
            <TableSimple
                tableRef={tableRef}
                rowIdKey="ID"
                rows={billsOfMaterialData || []}
                loading={loading}
                columsLabelPrefix="billsOfMaterial.table"
                allColumns={allColumns}
                initSort={{ column: 'ID', direction: 'DESC' }}
                rowActions={[
                    {
                        icon: <VerticalSplitIcon />,
                        label: translate('billsOfMaterial.action.billOfMaterialsDetail'),
                        getLink: (row) =>
                            idProduct
                                ? `/products/${idProduct}/bill_of_materials/${encodeURIComponent(row.ID)}/`
                                : `/products/bill_of_materials/${encodeURIComponent(row.ID)}/`,
                        hiddenFce: () => !permissionDetail,
                    },
                ]}
                selectOptions={{
                    boolNumberCodes,
                }}
                heightVariant="top-tab"
                fullHeight
                customRender={customRender}
                optionsSessionName="billsOfMaterialsTable"
            />
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    action={confirmParams.action}
                    onClose={(): any => setConfirmParams(null)}
                />
            )}
        </>
    );
};

export default BillsOfMaterialsListContainer;
